import { Download, LoaderCircle, Pencil, Search, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { BusinessRulesPanel } from "@/components/BusinessRulesPanel";

const PAGE_SIZE = 200;

const PRODUCT_PRESETS = [
  {
    key: "sky-1a07",
    label: "SKY-1A07 · 7000 BTU (courant)",
    reference: "SKY-1A07",
    description: "Fourniture de climatiseur mobile 7000 BTU - Dispositif Été 2026 Enseignement",
  },
  {
    key: "skyr-1c-09",
    label: "SKYR-1C-09 · 9000 BTU (ancien)",
    reference: "SKYR-1C-09",
    description: "Fourniture de climatiseurs mobiles 9000 BTU - Dispositif Été 2026 Enseignement",
  },
  { key: "custom", label: "Autre (libre)", reference: "", description: "" },
];

function detectPresetKey(reference, description) {
  const match = PRODUCT_PRESETS.find(
    (preset) => preset.reference === reference && preset.description === description,
  );
  return match ? match.key : "custom";
}

function lastRevisionProduct(history) {
  const entries = history || [];
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry && entry.product_reference) {
      return { reference: entry.product_reference, description: entry.product_description || "" };
    }
  }
  return null;
}

export default function QuoteCorrectionPage() {
  const [treatments, setTreatments] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState({});
  const defaultCommentRef = useRef("");
  const targetTreatmentId = new URLSearchParams(window.location.search).get("tid");

  function openCorrection(treatment) {
    setEditing(treatment);
    const lastProduct = lastRevisionProduct(treatment.quote_history);
    const productRef = lastProduct?.reference || PRODUCT_PRESETS[0].reference;
    const productDesc = lastProduct?.description || PRODUCT_PRESETS[0].description;
    setRevision({
      establishment_count: treatment.declared_establishment_count ?? 0,
      quantity_per_establishment: 10,
      unit_price_ht: 330,
      vat_rate: 20,
      recipient_email: treatment.recipient_email || "",
      recipient_name: treatment.original_lead_title || treatment.commune_name || "",
      recipient_address: treatment.recipient_address || "",
      recipient_postal: treatment.recipient_postal || treatment.postal_code || "",
      recipient_city: treatment.recipient_city || treatment.commune_name || "",
      product_preset_key: detectPresetKey(productRef, productDesc),
      product_reference: productRef,
      product_description: productDesc,
      comment: defaultCommentRef.current,
      reason: "Correction commerciale",
    });
    window.setTimeout(() => document.getElementById("quote-correction-panel")?.scrollIntoView({ behavior: "smooth" }), 0);
  }

  const fetchPage = async (offset, q, replace) => {
    const params = { status: "termine", limit: PAGE_SIZE, offset };
    if (q) params.q = q;
    const { data } = await api.get("/enrichments", { params });
    setTreatments((current) => (replace ? data.items : [...current, ...data.items]));
    setTotal(data.total);
    setHasMore(data.has_more);
    return data;
  };

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        defaultCommentRef.current = data.quote?.default_quote_comment || "";
      })
      .catch(() => {})
      .finally(() => {
        fetchPage(0, "", true).then((data) => {
          const selected = data.items.find((item) => item.id === targetTreatmentId);
          if (selected) openCorrection(selected);
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchPage(0, query.trim(), true).catch(() => toast.error("Impossible de rafraîchir la liste."));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchPage(treatments.length, query.trim(), false);
    } catch {
      toast.error("Impossible de charger la suite.");
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = async (send) => {
    if (revision.establishment_count === "" || revision.establishment_count === null || Number(revision.establishment_count) < 0) {
      toast.error("Saisissez la quantité déclarée avant de générer le devis.");
      return;
    }
    if (!revision.quantity_per_establishment || Number(revision.quantity_per_establishment) < 1) {
      toast.error("Le nombre de climatiseurs par établissement doit être supérieur ou égal à 1.");
      return;
    }
    setBusy(true);
    try {
      const path = send ? `/quotes/${editing.id}/revision/send` : `/quotes/${editing.id}/revision`;
      const { product_preset_key, ...payload } = revision;
      const { data } = await api.post(path, payload);
      toast.success(send ? `Devis ${data.number} renvoyé.` : `Devis ${data.number} généré.`);
      if (!send) window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${editing.id}/download`, "_blank", "noopener,noreferrer");
      if (send) setEditing(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Le devis n’a pas pu être traité.");
    } finally { setBusy(false); }
  };

  return (
    <section className="page" data-testid="quote-review-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">DEVIS</p>
          <h1 data-testid="quote-correction-title">Validation commerciale</h1>
          <p className="page-subtitle" data-testid="quote-correction-subtitle">
            Ajustez la quantité déclarée et le nombre de climatiseurs par établissement (défaut : 10).
          </p>
        </div>
      </header>
      <BusinessRulesPanel />
      <section className="data-panel" data-testid="quote-treatment-list">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">FICHES ENRICHIES</p>
            <h2>Devis à préparer</h2>
          </div>
          <div className="journal-controls">
            <div className="commune-search" data-testid="quote-search-wrap">
              <Search size={15} />
              <input
                type="search"
                placeholder="Rechercher une commune, un e-mail…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="quote-search-input"
              />
            </div>
            <span className="table-count" data-testid="quote-treatment-count">
              {treatments.length} / {total} fiche(s){query && " filtrées"}
            </span>
          </div>
        </div>
        <div className="table-wrap">
          <table className="quote-treatment-table" data-testid="quote-treatment-table">
            <thead>
              <tr>
                <th>Commune</th>
                <th>Quantité déclarée</th>
                <th>Climatiseurs</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {treatments.map((item) => (
                <tr key={item.id}>
                  <td data-label="Commune" data-testid={`quote-commune-${item.id}`}>{item.original_lead_title || item.commune_name}</td>
                  <td data-label="Quantité déclarée" data-testid={`quote-declared-count-${item.id}`}>{item.declared_establishment_count ?? "À renseigner"}</td>
                  <td data-label="Climatiseurs" data-testid={`quote-total-quantity-${item.id}`}>{item.declared_establishment_count === undefined || item.declared_establishment_count === null ? "À recalculer" : item.total_quantity}</td>
                  <td data-label="Action">
                    <button className="secondary-button" type="button" onClick={() => openCorrection(item)} data-testid={`open-quote-correction-${item.id}`}>
                      <Pencil size={16} />Corriger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="load-more-row">
            <button className="secondary-button" type="button" onClick={loadMore} disabled={loadingMore} data-testid="load-more-quotes-button">
              {loadingMore ? <LoaderCircle className="spinning" size={16} /> : null}
              Charger plus ({total - treatments.length} restantes)
            </button>
          </div>
        )}
      </section>
      {editing && (
        <section className="settings-section" id="quote-correction-panel" data-testid="quote-correction-panel">
          <div className="section-title">
            <div>
              <p className="eyebrow">CORRECTION</p>
              <h2 data-testid="quote-correction-commune">{editing.original_lead_title || editing.commune_name}</h2>
            </div>
          </div>
          <div className="form-grid">
            <label>Quantité déclarée<input type="number" min="0" value={revision.establishment_count} onChange={(e) => setRevision({ ...revision, establishment_count: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="establishment-count-input" /></label>
            <label>Climatiseurs par établissement<input type="number" min="1" value={revision.quantity_per_establishment} onChange={(e) => setRevision({ ...revision, quantity_per_establishment: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="quantity-per-establishment-input" /></label>
            <label>Prix HT<input type="number" value={revision.unit_price_ht} onChange={(e) => setRevision({ ...revision, unit_price_ht: Number(e.target.value) })} data-testid="revision-unit-price-input" /></label>
            <label>TVA (%)<input type="number" value={revision.vat_rate} onChange={(e) => setRevision({ ...revision, vat_rate: Number(e.target.value) })} data-testid="revision-vat-rate-input" /></label>
            <label>Destinataire e-mail<input type="email" value={revision.recipient_email} onChange={(e) => setRevision({ ...revision, recipient_email: e.target.value })} data-testid="revision-recipient-email-input" /></label>
            <label className="form-span-two">Nom du destinataire<input value={revision.recipient_name} onChange={(e) => setRevision({ ...revision, recipient_name: e.target.value })} data-testid="revision-recipient-name-input" placeholder="Ex. Mairie de Simard" /></label>
            <label className="form-span-two">Adresse<input value={revision.recipient_address} onChange={(e) => setRevision({ ...revision, recipient_address: e.target.value })} data-testid="revision-recipient-address-input" placeholder="Ex. 11 rue Lejeune" /></label>
            <label>Code postal<input value={revision.recipient_postal} onChange={(e) => setRevision({ ...revision, recipient_postal: e.target.value })} data-testid="revision-recipient-postal-input" /></label>
            <label>Ville<input value={revision.recipient_city} onChange={(e) => setRevision({ ...revision, recipient_city: e.target.value })} data-testid="revision-recipient-city-input" /></label>
            <label className="form-span-two">Motif<textarea value={revision.reason} onChange={(e) => setRevision({ ...revision, reason: e.target.value })} data-testid="revision-reason-input" /></label>
            <label className="form-span-two">Commentaire affiché sur le devis (encart en bas du PDF)<textarea rows={3} value={revision.comment ?? ""} onChange={(e) => setRevision({ ...revision, comment: e.target.value })} placeholder="Laissez vide pour ne rien afficher sur ce devis." data-testid="revision-comment-input" /></label>
            <label className="form-span-two">
              Modèle produit
              <select
                value={revision.product_preset_key}
                onChange={(e) => {
                  const key = e.target.value;
                  const preset = PRODUCT_PRESETS.find((p) => p.key === key);
                  if (!preset) return;
                  if (key === "custom") {
                    setRevision({ ...revision, product_preset_key: key, product_reference: "", product_description: "" });
                  } else {
                    setRevision({
                      ...revision,
                      product_preset_key: key,
                      product_reference: preset.reference,
                      product_description: preset.description,
                    });
                  }
                }}
                data-testid="product-preset-select"
              >
                {PRODUCT_PRESETS.map((preset) => (
                  <option key={preset.key} value={preset.key}>{preset.label}</option>
                ))}
              </select>
            </label>
            <label>
              Référence produit
              <input
                value={revision.product_reference}
                disabled={revision.product_preset_key !== "custom"}
                onChange={(e) => setRevision({ ...revision, product_reference: e.target.value })}
                data-testid="revision-product-reference-input"
              />
            </label>
            <label>
              Description produit
              <input
                value={revision.product_description}
                disabled={revision.product_preset_key !== "custom"}
                onChange={(e) => setRevision({ ...revision, product_description: e.target.value })}
                data-testid="revision-product-description-input"
              />
            </label>
          </div>
          <p className="source-note" data-testid="revision-total-preview">
            Total climatiseurs : <strong>{Number(revision.establishment_count || 0) * Number(revision.quantity_per_establishment || 0)}</strong> ({Number(revision.establishment_count || 0)} × {Number(revision.quantity_per_establishment || 0)})
          </p>
          <div className="button-row">
            <button className="secondary-button" disabled={busy} onClick={() => submit(false)} data-testid="generate-revision-button"><Download size={16} />Régénérer V2</button>
            <button className="primary-button" disabled={busy} onClick={() => submit(true)} data-testid="resend-revision-button"><Send size={16} />Régénérer et renvoyer</button>
          </div>
        </section>
      )}
    </section>
  );
}
